import React, { useState } from 'react';
import { api } from './lib/api';

interface LiasseError {
  code: string;
  message: string;
  critical: boolean;
}

interface CheckResult {
  valid: boolean;
  errors: LiasseError[];
}

interface LiasseFiscaleProps {
  entrepriseId: string;
}

const LiasseFiscale: React.FC<LiasseFiscaleProps> = ({ entrepriseId }) => {
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runChecks = async () => {
    setLoading(true);
    setError(null);
    try {
      const data: CheckResult = await api.liasse.check(entrepriseId);
      setCheckResult(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const hasCriticalErrors = checkResult?.errors.some(e => e.critical) ?? false;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Liasse fiscale</h2>

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={runChecks} disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {loading ? 'Vérification...' : 'Vérifier la cohérence'}
        </button>
        <button
          onClick={() => window.open(api.liasse.get2033(entrepriseId), '_blank')}
          disabled={hasCriticalErrors}
          title={hasCriticalErrors ? 'Corrigez les erreurs critiques avant de générer le PDF' : ''}
          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Télécharger Liasse 2033 (PDF)
        </button>
        <button
          onClick={() => window.open(api.liasse.get2031(entrepriseId), '_blank')}
          disabled={hasCriticalErrors}
          title={hasCriticalErrors ? 'Corrigez les erreurs critiques avant de générer le PDF' : ''}
          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Télécharger Déclaration 2031 (PDF)
        </button>
      </div>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur : {error}</div>}

      {checkResult && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className={`text-lg font-semibold mb-3 ${checkResult.valid ? 'text-green-600' : 'text-red-600'}`}>
            {checkResult.valid ? 'Vérification réussie' : 'Erreurs détectées'}
          </h3>
          {checkResult.errors.length === 0 ? (
            <p className="text-sm text-gray-700">Aucune anomalie détectée. La liasse peut être générée.</p>
          ) : (
            <ul className="space-y-2">
              {checkResult.errors.map((err, i) => (
                <li key={i} className={`text-sm px-3 py-2 rounded-lg ${err.critical ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  <strong>[{err.critical ? 'CRITIQUE' : 'AVERTISSEMENT'}]</strong> {err.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LiasseFiscale;
